import { useCallback, useEffect, useState } from 'react';

import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';

import { IdentificationMethod } from '@cobliteam/common-types';

import Icon from '@cobliteam/ui/icons';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import DropdownBody from '@cobliteam/ui/components/Dropdown/components/DropdownBody';

interface MultiSelectFaceIdIdentificationMethodProps {
  size?: 'condensed' | 'regular';
  selectedOptions: IdentificationMethod[];
  onChange: (identificationMethod: IdentificationMethod[]) => void;
}

const getIdentificationMethodLabel = (identificationMethod: IdentificationMethod) => {
  switch (identificationMethod) {
    case IdentificationMethod.WITHOUT_IDENTIFICATION:
      return 'Não identificado';
    case IdentificationMethod.FACE_IDENTIFICATION:
      return 'Identificado pela câmera';
    case IdentificationMethod.USER_CONFIRMATION:
      return 'Confirmado pelo usuário da frota';
    case IdentificationMethod.USER_EDIT:
      return 'Identificado pelo usuário da frota';
    default:
      return 'Viagem sem imagem';
  }
};

const MultiSelectFaceIdIdentificationMethod = ({ selectedOptions, onChange, size = 'regular' }: MultiSelectFaceIdIdentificationMethodProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  const hasNotChanged = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const handleClear = useCallback(() => {
    setDraft([]);
    onChange([]);
  }, [onChange]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (item: IdentificationMethod) => {
      const existItem = draft.includes(item);
      const newDraft = existItem ? draft.filter((value) => value !== item) : [...draft, item];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='condensed' disabled={draft?.length === 0} onClick={handleClear}>
              Limpar filtro
            </Button>
          </Dropdown.Header>

          <DropdownBody className='ml-5'>
            <Dropdown.List
              heightItem={32}
              heightList={300}
              data={Object.values(IdentificationMethod)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: IdentificationMethod) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getIdentificationMethodLabel(item)}</Label>
                </div>
              )}
            />
          </DropdownBody>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='text' full size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='condensed' full onClick={handleApply} disabled={hasNotChanged}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<Icon name='smile-plus' color='primary' />}>
        Identificado por {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectFaceIdIdentificationMethod;
